import { useTheme } from 'contexts'
import React, { useState } from 'react'
import Select, { StylesConfig } from 'react-select'

import { Box } from 'widgets/Box'
import { ControlProps } from 'widgets/Field/types'
import { Text } from 'widgets/Text'

interface DropdownProps extends ControlProps {
  isSearchable?: boolean
  onClose?: () => void
  onOpen?: () => void
  options?: any[]
  touched?: boolean
  error?: string
  placeholderText?: string
  multiSelect?: boolean
  onMultiSelectChange?: (value: string[]) => void
}

export const Dropdown = ({
  isSearchable = true,
  name,
  onChange,
  onClose = () => null,
  onOpen = () => null,
  options = [],
  value,
  error,
  touched,
  placeholderText,
  multiSelect = false,
  onMultiSelectChange,
}: DropdownProps) => {
  const selectedValues = options
    ?.map((opt) => {
      return value?.includes(opt.value) ? opt : undefined
    })
    .filter((opt) => opt !== undefined)

  const theme = useTheme()
  const [selected, setSelected] = useState(selectedValues)
  const showError = error && touched

  const customStyle: StylesConfig = {
    control: (styles) => ({
      ...styles,
      color: 'black',
      border: 'none',
      fontFamily: 'Poppins',
      fontSize: theme.fonts.sizes.sm,
      fontWeight: theme.fonts.weights.medium,
      backgroundColor: 'white',
    }),
    option: (styles, state) => ({
      ...styles,
      color: 'black',
      fontFamily: 'Poppins',
      fontSize: theme.fonts.sizes.sm,
      fontWeight: theme.fonts.weights.medium,
      backgroundColor: state.isSelected ? theme.colors.v2primary : 'white',
    }),
    placeholder: (styles) => ({
      ...styles,
      color: theme.colors.greyDark,
      fontWeight: theme.fonts.weights.medium,
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: 'none',
    }),
    multiValue: (styles) => {
      return {
        ...styles,
        backgroundColor: theme.colors.v2primary,
      }
    },
    multiValueLabel: (styles) => ({
      ...styles,
      color: theme.colors.white,
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: theme.colors.white,
    }),
  }

  const setMultiSelectedValues = (newValue: any) => {
    const values = newValue.map((val: any) => val.value)
    const draftIndex = values.indexOf('draft')
    const isDraft =
      (draftIndex > 0 && values.length > 1) || (draftIndex === 0 && values.length === 1)
    const selectedValue = isDraft
      ? [{ ...newValue[draftIndex] }]
      : newValue.filter((val: any) => val.value !== 'draft')
    setSelected(selectedValue)
    const finalValues = selectedValue.map((val: any) => val.value)
    onMultiSelectChange && onMultiSelectChange(finalValues ?? [])
  }

  return (
    <>
      <Select
        closeMenuOnSelect={!multiSelect}
        isMulti={multiSelect}
        menuPortalTarget={document.body}
        menuPosition='fixed'
        placeholder={placeholderText}
        isSearchable={isSearchable}
        options={options}
        onMenuOpen={onOpen}
        onMenuClose={onClose}
        styles={customStyle}
        value={selected}
        defaultValue={!multiSelect ? options[0] : undefined}
        onChange={(newValue: any) => {
          if (multiSelect) {
            setMultiSelectedValues(newValue)
          } else {
            onChange(name)(newValue.value)
            setSelected(newValue)
          }
        }}
      />
      {showError ? (
        <Box padding={{ vertical: 'xs' }}>
          <Text type='error' fontWeight='medium' fontSize='sm'>
            {error}
          </Text>
        </Box>
      ) : null}
    </>
  )
}
